/**
 * Render snapshot — structured JSON of what's currently on the user's
 * screen. The AI's primary view-awareness signal; screenshots are the
 * pixel-perfect fallback for things text can't capture (chart shapes,
 * colors, custom canvas renders), but for "what's on the screen" this
 * JSON is faster, cheaper, and gives the AI structured data it can
 * reason over natively.
 *
 * Captures:
 *   - Route info (path, page_kind, params)
 *   - Navigation (sidebar items, breadcrumbs)
 *   - Header (title + breadcrumb context)
 *   - Sections (h2/h3 + immediate children context)
 *   - Cards (titled boxes with their primary content)
 *   - Lists (tables / lists with row counts + sample of items)
 *   - Badges + status indicators (text + tone class hint)
 *   - Forms (active inputs with current values, no passwords)
 *   - Errors (anything in an error styling state)
 *   - AI-composed panels (verbatim PanelSpec from window.__pageContext
 *     if the page exposes it — that's already JSON, just pass through)
 *
 * Output is bounded to roughly 5KB stringified — enough to capture
 * the structure of a complex dashboard without blowing the prompt.
 *
 * Pure DOM extraction — no html2canvas, no canvas, no cross-origin
 * issues. Runs in ~10-20ms vs html2canvas's 200-500ms.
 */

export interface RenderSnapshot {
  /** Route info from window.location + react-router-style data attrs. */
  route: {
    path: string;
    page_kind: string | null;
    workspace_id: string | null;
  };
  /** Navigation context — sidebar + breadcrumbs. */
  navigation: {
    sidebar_items: Array<{ label: string; href: string; active: boolean }>;
    breadcrumbs: Array<{ label: string; href?: string }>;
  };
  /** Header info — title + subtitle + status badges. */
  header: {
    title: string | null;
    subtitle: string | null;
    badges: Array<{ label: string; tone: string }>;
  };
  /** Cards rendered on the page (titled boxes with content). */
  cards: Array<{
    title: string;
    summary?: string;
    badges?: Array<{ label: string; tone: string }>;
  }>;
  /** Lists / tables with row counts + sample entries. */
  lists: Array<{
    id?: string;
    title?: string;
    row_count: number;
    sample: Array<{ label: string; subtitle?: string; href?: string }>;
  }>;
  /** Active form inputs (values included, passwords/sensitive excluded). */
  forms: Array<{
    name?: string;
    fields: Array<{ label: string; value: string; type: string }>;
  }>;
  /** Anything in an error / red state. */
  errors: Array<{ source: string; message: string }>;
  /** AI-composed panels currently mounted (their spec JSON if the page exposes). */
  ai_panels: Array<{
    page_kind: string;
    instance_id: string;
    spec_summary?: string;
  }>;
  /** Time the snapshot was captured. */
  captured_at_ms: number;
}

const TEXT_CAP = 200;
const LIST_SAMPLE_CAP = 8;
const CARD_CAP = 12;
const SIDEBAR_CAP = 16;

function trim(s: string | null | undefined, cap = TEXT_CAP): string {
  if (!s) return "";
  const t = s.trim().replace(/\s+/g, " ");
  return t.length > cap ? t.slice(0, cap - 1) + "…" : t;
}

function toneFromClasses(el: Element): string {
  const cls = el.className && typeof el.className === "string" ? el.className.toLowerCase() : "";
  if (/text-red|bg-red|text-rose|alert|danger|error/.test(cls)) return "alert";
  if (/text-amber|text-yellow|warning/.test(cls)) return "warning";
  if (/text-green|text-emerald|success/.test(cls)) return "success";
  if (/text-blue|text-indigo|primary/.test(cls)) return "primary";
  if (/text-stone|text-gray|text-zinc|muted/.test(cls)) return "muted";
  return "default";
}

function extractNavigation(): RenderSnapshot["navigation"] {
  const nav: RenderSnapshot["navigation"] = { sidebar_items: [], breadcrumbs: [] };

  // Sidebar: <aside> or <nav> with anchors. Pick the first nav-like
  // container with multiple <a> children.
  const navCandidates = Array.from(
    document.querySelectorAll("aside nav a, nav.sidebar a, [data-sidebar] a, aside a")
  ).slice(0, SIDEBAR_CAP);
  const currentPath = location.pathname;
  for (const a of navCandidates) {
    const href = (a as HTMLAnchorElement).getAttribute("href") ?? "";
    const label = trim(a.textContent, 60);
    if (!label || !href) continue;
    nav.sidebar_items.push({
      label,
      href,
      active: href === currentPath || (href !== "/" && currentPath.startsWith(href)),
    });
  }

  // Breadcrumbs: standard pattern via [aria-label="breadcrumb"] or
  // .breadcrumb class.
  const breadcrumbRoot = document.querySelector(
    '[aria-label="breadcrumb"], [aria-label="Breadcrumb"], .breadcrumb, nav.breadcrumb'
  );
  if (breadcrumbRoot) {
    const items = Array.from(breadcrumbRoot.querySelectorAll("li, a")).slice(0, 8);
    for (const el of items) {
      const label = trim(el.textContent, 60);
      if (!label) continue;
      const href = el.tagName === "A" ? (el as HTMLAnchorElement).getAttribute("href") : undefined;
      nav.breadcrumbs.push({ label, href: href ?? undefined });
    }
  }

  return nav;
}

function extractHeader(root: Element): RenderSnapshot["header"] {
  const h1 = root.querySelector("h1");
  const subtitle = h1?.parentElement?.querySelector("h1 + *");
  const badgeEls = Array.from(root.querySelectorAll("h1 ~ [data-badge], h1 ~ .badge, .page-header [class*='badge']")).slice(0, 6);
  return {
    title: h1 ? trim(h1.textContent) : null,
    subtitle: subtitle ? trim(subtitle.textContent) : null,
    badges: badgeEls.map((el) => ({ label: trim(el.textContent, 40), tone: toneFromClasses(el) })).filter((b) => b.label),
  };
}

function extractCards(root: Element): RenderSnapshot["cards"] {
  // Cards are containers with a heading-like child (h2-h5, .card-title,
  // or a font-semibold/font-bold element near the top). Capture title +
  // a 1-line summary of the first text content beneath it.
  const cardEls = Array.from(
    root.querySelectorAll(
      "[data-card], .card, [class*='rounded-'][class*='border'], [class*='shadow']"
    )
  ).slice(0, CARD_CAP * 3); // candidates; we filter below
  const seen = new Set<string>();
  const out: RenderSnapshot["cards"] = [];
  for (const card of cardEls) {
    if (out.length >= CARD_CAP) break;
    const titleEl = card.querySelector("h2, h3, h4, h5, [data-card-title], .card-title");
    const title = trim(titleEl?.textContent, 120);
    if (!title || seen.has(title.toLowerCase())) continue;
    seen.add(title.toLowerCase());
    // Summary: first non-heading text child, capped at 160 chars.
    let summary: string | undefined;
    for (const child of Array.from(card.children)) {
      if (child === titleEl) continue;
      const text = (child.textContent ?? "").trim();
      if (text && text !== title) {
        summary = trim(text, 160);
        break;
      }
    }
    out.push({ title, summary });
  }
  return out;
}

function extractLists(root: Element): RenderSnapshot["lists"] {
  const out: RenderSnapshot["lists"] = [];

  // Tables
  const tables = Array.from(root.querySelectorAll("table")).slice(0, 4);
  for (const table of tables) {
    const titleEl = table.previousElementSibling?.matches?.("h2, h3, h4, h5") ? table.previousElementSibling : null;
    const rows = Array.from(table.querySelectorAll("tbody tr"));
    const sample: Array<{ label: string; subtitle?: string }> = [];
    for (const row of rows.slice(0, LIST_SAMPLE_CAP)) {
      const cells = Array.from(row.querySelectorAll("td"));
      const label = trim(cells[0]?.textContent, 80);
      const subtitle = cells[1] ? trim(cells[1].textContent, 80) : undefined;
      if (label) sample.push({ label, subtitle });
    }
    out.push({
      title: titleEl ? trim(titleEl.textContent, 80) : undefined,
      row_count: rows.length,
      sample,
    });
  }

  // <ul>/<ol> lists with anchor children (skip tiny lists < 3 items)
  const lists = Array.from(root.querySelectorAll("ul, ol")).slice(0, 6);
  for (const list of lists) {
    const items = Array.from(list.querySelectorAll(":scope > li"));
    if (items.length < 3) continue;
    if (out.length >= 6) break;
    const titleEl = list.previousElementSibling?.matches?.("h2, h3, h4, h5") ? list.previousElementSibling : null;
    const sample: Array<{ label: string; subtitle?: string; href?: string }> = [];
    for (const li of items.slice(0, LIST_SAMPLE_CAP)) {
      const a = li.querySelector("a");
      const label = trim(li.textContent, 100);
      if (label) {
        sample.push({
          label,
          href: a ? (a as HTMLAnchorElement).getAttribute("href") ?? undefined : undefined,
        });
      }
    }
    out.push({
      title: titleEl ? trim(titleEl.textContent, 80) : undefined,
      row_count: items.length,
      sample,
    });
  }

  return out;
}

function extractForms(root: Element): RenderSnapshot["forms"] {
  const forms = Array.from(root.querySelectorAll("form")).slice(0, 3);
  return forms.map((form) => {
    const inputs = Array.from(form.querySelectorAll("input, textarea, select")).slice(0, 12);
    return {
      name: (form as HTMLFormElement).name || (form as HTMLFormElement).id || undefined,
      fields: inputs
        .map((input) => {
          const el = input as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
          const type = (el as HTMLInputElement).type ?? el.tagName.toLowerCase();
          // Hide sensitive values entirely
          if (type === "password" || type === "hidden") {
            return { label: el.name || "", value: "[redacted]", type };
          }
          const labelEl = el.id
            ? document.querySelector(`label[for="${el.id}"]`)
            : el.closest("label");
          const label = trim(labelEl?.textContent ?? el.getAttribute("aria-label") ?? el.name ?? "", 60);
          const value = trim(String(el.value ?? ""), 120);
          return { label, value, type };
        })
        .filter((f) => f.label),
    };
  });
}

function extractErrors(root: Element): RenderSnapshot["errors"] {
  // STRICT selector — only catch genuine error UI:
  //   - [role="alert"]                (ARIA error semantics)
  //   - [data-error]                  (explicit opt-in marker)
  //   - .error / .error-message       (semantic class names)
  //   - .text-red-* / [class*="bg-red"] LIMITED to elements with
  //     additional error-shaped class names (alert/banner/notification/
  //     toast) so single-character party badges (R/D/I) don't trip it.
  //
  // The prior selector caught every text-red-500 span which mis-flagged
  // party-letter badges as errors on every dashboard render.
  const explicit = Array.from(
    root.querySelectorAll(
      '[role="alert"], [data-error], .error, .error-message, .alert-error, .toast-error'
    )
  );
  const colorBased = Array.from(
    root.querySelectorAll<HTMLElement>('[class*="bg-red"], .text-red-500, .text-red-600, .text-red-700')
  ).filter((el) => {
    const cls = el.className && typeof el.className === "string" ? el.className.toLowerCase() : "";
    // Require an error-shaped class alongside the red — banner/alert/
    // notification/toast/message. Excludes badges, chips, party letters.
    if (!/alert|banner|notification|toast|error|warning|callout/.test(cls)) return false;
    // Also exclude tiny elements (single char / 2-char codes like "R"
    // or "D") — real errors have prose.
    const text = (el.textContent ?? "").trim();
    if (text.length < 8) return false;
    return true;
  });
  const errorEls = Array.from(new Set([...explicit, ...colorBased])).slice(0, 5);
  return errorEls
    .map((el) => ({
      source: el.getAttribute("data-error-source") ?? el.tagName.toLowerCase(),
      message: trim(el.textContent, 200),
    }))
    .filter((e) => e.message && e.message.length >= 8);
}

function extractAIPanels(): RenderSnapshot["ai_panels"] {
  // <PanelRenderer/> mount points emit data-ai-panel and data-instance-id
  // when they render an AI-composed spec. Fall back to detecting via
  // window.__pageContext.aiPanels if the page exposes it.
  const mountEls = Array.from(document.querySelectorAll("[data-ai-panel]"));
  const out: RenderSnapshot["ai_panels"] = [];
  for (const el of mountEls.slice(0, 6)) {
    const page_kind = el.getAttribute("data-ai-panel") ?? "unknown";
    const instance_id = el.getAttribute("data-instance-id") ?? "default";
    // 1-line summary: first heading in the rendered spec.
    const heading = el.querySelector("h1, h2, h3");
    const spec_summary = heading ? trim(heading.textContent, 120) : undefined;
    out.push({ page_kind, instance_id, spec_summary });
  }
  return out;
}

export function extractRenderSnapshot(): RenderSnapshot | null {
  if (typeof document === "undefined") return null;
  try {
    const root: Element =
      document.querySelector("main") ??
      document.querySelector("[role='main']") ??
      document.body;

    const ctx = (window as any).__pageContext as
      | { kind?: string; workspace_id?: string }
      | null
      | undefined;

    return {
      route: {
        path: location.pathname,
        page_kind: ctx?.kind ?? null,
        workspace_id:
          ctx?.workspace_id ??
          new URLSearchParams(location.search).get("workspace_id"),
      },
      navigation: extractNavigation(),
      header: extractHeader(root),
      cards: extractCards(root).slice(0, CARD_CAP),
      lists: extractLists(root),
      forms: extractForms(root),
      errors: extractErrors(root),
      ai_panels: extractAIPanels(),
      captured_at_ms: Date.now(),
    };
  } catch {
    return null;
  }
}

/**
 * Coerce to a JSON string for upload. Bounded — if the serialized
 * snapshot exceeds maxBytes we drop the largest sections first
 * (cards, then lists, then nav) until it fits.
 */
export function serializeSnapshot(snap: RenderSnapshot, maxBytes = 8_000): string {
  let json = JSON.stringify(snap);
  if (json.length <= maxBytes) return json;
  // Progressive prune
  const pruned: RenderSnapshot = { ...snap };
  pruned.cards = pruned.cards.slice(0, 6);
  json = JSON.stringify(pruned);
  if (json.length <= maxBytes) return json;
  pruned.lists = pruned.lists.slice(0, 2).map((l) => ({ ...l, sample: l.sample.slice(0, 3) }));
  json = JSON.stringify(pruned);
  if (json.length <= maxBytes) return json;
  pruned.navigation = {
    sidebar_items: pruned.navigation.sidebar_items.filter((s) => s.active),
    breadcrumbs: pruned.navigation.breadcrumbs,
  };
  json = JSON.stringify(pruned);
  return json.length <= maxBytes ? json : json.slice(0, maxBytes - 8) + '"...]}';
}
